<template>
  <md-card>
    <md-card-header>
      <h4 class="title">Reservations</h4>
      <p class="category">Restaurant reservations for selected restaurant</p>
    </md-card-header>
    <reservations-toolbar
      :include-past="includePast"
      :loading="loading"
      :action-loading="actionLoading"
      :has-rows="Boolean(reservationRows.length)"
      @update:includePast="includePast = $event"
      @add="openAddDialog"
      @download="downloadReservationsCsv"
    />

    <reservations-grouped-list
      :loading="loading"
      :load-error="loadError"
      :grouped-reservations="groupedReservations"
      :expanded-notes="expandedNotes"
      empty-description="No reservations are currently linked to this restaurant."
    >
      <template slot="header">
        <md-table-cell class="res-col-number">Reservation #</md-table-cell>
        <md-table-cell class="res-col-confirmed">Confirmed</md-table-cell>
        <md-table-cell class="res-col-date">Date</md-table-cell>
        <md-table-cell class="res-col-night">Night of</md-table-cell>
        <md-table-cell class="res-col-time">Booking Time</md-table-cell>
        <md-table-cell
          class="res-col-actions col-actions actions-header col-right"
          >Actions</md-table-cell
        >
      </template>

      <template slot="row" slot-scope="{ item }">
        <restaurant-reservation-row
          :key="item.key"
          :item="item"
          :is-editing="isRowEditing(item.key)"
          :is-notes-expanded="isNotesExpanded(item.key)"
          :inline-edit="inlineEdit"
          :action-loading="actionLoading"
          @toggle-notes="toggleNotes(item.key)"
          @save="saveInlineEdit(item)"
          @cancel="cancelInlineEdit"
          @open-manifest="goToManifest(item)"
          @edit="startInlineEdit(item)"
          @delete="deleteReservation(item)"
        />
      </template>

      <template slot="notes" slot-scope="{ item }">
        <div v-if="isRowEditing(item.key)" class="summary-note">
          <md-field class="inline-field inline-notes">
            <md-textarea v-model="inlineEdit.notes"></md-textarea>
          </md-field>
        </div>
        <div v-else class="summary-note">{{ item.notes }}</div>
      </template>
    </reservations-grouped-list>

    <add-reservation-dialog
      :active="showAddDialog"
      :departure-list-id="departureListId"
      :departure-search="addDepartureSearch"
      :selected-departure-id="addForm.departure_id"
      :departures-loading="departuresLoading"
      :action-loading="actionLoading"
      :available-departures="availableDepartures"
      :rows="addRows"
      @update:active="showAddDialog = $event"
      @update:departureSearch="addDepartureSearch = $event"
      @apply-departure-search="applyAddDepartureSearch"
      @add-row="addAddRow"
      @remove-row="removeAddRow"
      @create-all-dates="createAllDatesReservations"
      @add="saveNewReservation"
    >
      <template slot="row-fields" slot-scope="{ row }">
        <div class="md-layout-item md-size-18 md-small-size-100">
          <md-field>
            <label>Reservation #</label>
            <md-input v-model="row.reservation_number" type="text"></md-input>
          </md-field>
        </div>
        <div class="md-layout-item md-size-12 md-small-size-100">
          <md-field>
            <label>Booking Time</label>
            <md-input v-model="row.time" type="time"></md-input>
          </md-field>
        </div>
        <div class="md-layout-item md-size-35 md-small-size-100">
          <md-field>
            <label>Notes</label>
            <md-input v-model="row.notes" type="text"></md-input>
          </md-field>
        </div>
        <div
          class="md-layout-item md-size-8 md-small-size-80 add-confirmed-cell"
        >
          <md-switch v-model="row.confirmed">Confirmed</md-switch>
        </div>
      </template>
    </add-reservation-dialog>
  </md-card>
</template>

<script>
import restaurantApi from "@/services/api/restaurant";
import confirm from "@/helpers/deleteConfirm";
import { downloadCsvFile } from "@/helpers/csv";
import {
  buildRestaurantReservationsCsv,
  getMissingDepartureDates,
  groupReservationsByDeparture,
  mapRestaurantReservationRows
} from "@/helpers/reservations";
import {
  ensureManifestArray,
  getDepartureManifest,
  mutateAndSaveDepartureManifest
} from "@/helpers/reservationManifest";
import AddReservationDialog from "./Reservations/AddReservationDialog";
import ReservationsGroupedList from "./Reservations/ReservationsGroupedList";
import ReservationsToolbar from "./Reservations/ReservationsToolbar";
import RestaurantReservationRow from "./Reservations/RestaurantReservationRow";
import reservationsTabShared from "./Reservations/reservationsTabShared";

export default {
  props: {
    restaurant: Object
  },
  mixins: [reservationsTabShared],
  components: {
    AddReservationDialog,
    ReservationsGroupedList,
    ReservationsToolbar,
    RestaurantReservationRow
  },

  computed: {
    departureListId() {
      return `restaurant-departure-list-${this._uid}`;
    },

    groupedReservations() {
      const formatDate =
        this.$options.filters && this.$options.filters.formatDate;
      return groupReservationsByDeparture(this.reservationRows, formatDate);
    }
  },

  data() {
    return {
      loading: false,
      loadError: null,
      reservationRows: [],
      includePast: false,
      expandedNotes: {},
      actionLoading: false,
      showAddDialog: false,
      departuresLoading: false,
      availableDepartures: [],
      addDepartureSearch: "",
      editingRowKey: null,
      inlineEdit: {
        departure_id: null,
        dinner_id: null,
        date: "",
        reservation_number: "",
        time: "",
        notes: "",
        confirmed: false
      },
      addForm: {
        departure_id: null
      },
      addRows: []
    };
  },

  methods: {
    async loadRowsFromRestaurantEndpoint() {
      const response = await restaurantApi.getRestaurantReservations(
        this.restaurant.id,
        this.includePast ? { include_past: 1 } : {}
      );
      const departureGroups = Array.isArray(response.data?.departures)
        ? response.data.departures
        : [];
      return mapRestaurantReservationRows(departureGroups, departure =>
        this.getTourText(departure)
      );
    },

    newAddRow(date = "") {
      return {
        date,
        reservation_number: "",
        time: "",
        notes: "",
        confirmed: false
      };
    },

    async saveNewReservation() {
      if (!this.addForm.departure_id) return;
      this.actionLoading = true;
      try {
        const rowsToSave = this.addRows.filter(row => row.date);
        if (!rowsToSave.length) throw new Error("Missing dates");

        await mutateAndSaveDepartureManifest(
          this.addForm.departure_id,
          manifest => {
            const dinners = ensureManifestArray(manifest, "dinners");
            rowsToSave.forEach(row => {
              dinners.push({
                date: row.date,
                restaurant_id: this.restaurant.id,
                reservation_number: row.reservation_number || null,
                time: row.time || "",
                notes: row.notes || "",
                confirmed: row.confirmed
              });
            });
          }
        );

        this.showAddDialog = false;
        await this.getRestaurantReservations();
        this.$notify({
          timeout: 2500,
          message: `${rowsToSave.length} reservation(s) added.`,
          icon: "thumb_up",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "success"
        });
      } catch (error) {
        this.$notify({
          timeout: 2500,
          message: "Unable to add reservation.",
          icon: "error",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "danger"
        });
      } finally {
        this.actionLoading = false;
      }
    },

    async createAllDatesReservations() {
      if (!this.addForm.departure_id) return;
      this.actionLoading = true;
      try {
        const departure = this.availableDepartures.find(
          item => item.id === this.addForm.departure_id
        );
        if (!departure) throw new Error("Departure not found");

        const manifest = await getDepartureManifest(this.addForm.departure_id);

        const existingDates = ensureManifestArray(manifest, "dinners")
          .filter(
            dinner =>
              Number(dinner.restaurant_id) === Number(this.restaurant.id) &&
              dinner.date
          )
          .map(dinner => dinner.date);

        const missingDateRows = getMissingDepartureDates(
          departure.start_date,
          departure.end_date,
          existingDates
        ).map(date => this.newAddRow(date));

        this.addRows = missingDateRows.length
          ? missingDateRows
          : [this.newAddRow(departure.start_date)];

        this.$notify({
          timeout: 2500,
          message: missingDateRows.length
            ? `${missingDateRows.length} date(s) added to the form.`
            : "All dates already exist for this restaurant.",
          icon: "info",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "info"
        });
      } catch (error) {
        this.$notify({
          timeout: 2500,
          message: "Unable to create reservation dates.",
          icon: "error",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "danger"
        });
      } finally {
        this.actionLoading = false;
      }
    },

    startInlineEdit(row) {
      if (!row || !row.canManage || this.actionLoading) return;
      this.editingRowKey = row.key;
      this.inlineEdit = {
        departure_id: row.departure_id,
        dinner_id: row.dinner_id,
        date: row.date || "",
        reservation_number: row.reservation_number || "",
        time: row.time || "",
        notes: row.notes || "",
        confirmed: Boolean(row.confirmed)
      };
      this.$set(this.expandedNotes, row.key, false);
    },

    cancelInlineEdit() {
      this.editingRowKey = null;
      this.inlineEdit = {
        departure_id: null,
        dinner_id: null,
        date: "",
        reservation_number: "",
        time: "",
        notes: "",
        confirmed: false
      };
    },

    async saveInlineEdit() {
      if (!this.inlineEdit.departure_id || !this.inlineEdit.dinner_id) return;
      this.actionLoading = true;
      try {
        await mutateAndSaveDepartureManifest(
          this.inlineEdit.departure_id,
          manifest => {
            const dinners = ensureManifestArray(manifest, "dinners");
            const dinnerIndex = dinners.findIndex(
              dinner => dinner.id === this.inlineEdit.dinner_id
            );
            if (dinnerIndex < 0) throw new Error("Reservation not found");

            const dinner = dinners[dinnerIndex];
            dinners.splice(dinnerIndex, 1, {
              ...dinner,
              date: this.inlineEdit.date,
              reservation_number: this.inlineEdit.reservation_number || null,
              time: this.inlineEdit.time || "",
              notes: this.inlineEdit.notes || "",
              confirmed: this.inlineEdit.confirmed
            });
          }
        );
        this.cancelInlineEdit();
        await this.getRestaurantReservations();
        this.$notify({
          timeout: 2500,
          message: "Reservation updated.",
          icon: "thumb_up",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "success"
        });
      } catch (error) {
        this.$notify({
          timeout: 2500,
          message: "Unable to update reservation.",
          icon: "error",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "danger"
        });
      } finally {
        this.actionLoading = false;
      }
    },

    deleteReservation(row) {
      if (!row.departure_id || !row.dinner_id) return;
      confirm.delete().then(async resp => {
        if (!resp.value) return;
        this.actionLoading = true;
        try {
          await mutateAndSaveDepartureManifest(row.departure_id, manifest => {
            manifest.dinners = ensureManifestArray(manifest, "dinners").filter(
              dinner => dinner.id !== row.dinner_id
            );
          });
          await this.getRestaurantReservations();
          this.$notify({
            timeout: 2500,
            message: "Reservation deleted.",
            icon: "cancel",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "danger"
          });
        } catch (error) {
          this.$notify({
            timeout: 2500,
            message: "Unable to delete reservation.",
            icon: "error",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "danger"
          });
        } finally {
          this.actionLoading = false;
        }
      });
    },

    downloadReservationsCsv() {
      if (!this.reservationRows.length) return;
      const sortedRows = this.sortRows([...this.reservationRows]);
      const getDayFromDate =
        this.$options.filters && this.$options.filters.getDayFromDate;
      downloadCsvFile(
        buildRestaurantReservationsCsv({
          rows: sortedRows,
          restaurantName: this.restaurant?.company,
          getDayFromDate
        })
      );
    },

    async getRestaurantReservations() {
      if (!this.restaurant || !this.restaurant.id) {
        this.reservationRows = [];
        this.cancelInlineEdit();
        return;
      }

      this.loading = true;
      this.loadError = null;
      this.expandedNotes = {};
      this.cancelInlineEdit();

      try {
        const rows = await this.loadRowsFromRestaurantEndpoint();
        this.reservationRows = this.sortRows(rows);
      } catch (error) {
        this.loadError = "Unable to load reservations.";
        this.reservationRows = [];
      } finally {
        this.loading = false;
      }
    }
  },

  watch: {
    includePast() {
      this.getRestaurantReservations();
    },
    "restaurant.id": {
      handler() {
        this.getRestaurantReservations();
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss" scoped>
@import "./Reservations/reservations.shared";

::v-deep .reservations-header,
::v-deep .summary-row {
  min-width: 900px;
}

.col-right {
  text-align: right;
}

::v-deep .summary-header .col-right,
::v-deep .summary-row .col-right {
  text-align: right;
}

::v-deep .summary-header .col-right .md-table-cell-container,
::v-deep .summary-row .col-right .md-table-cell-container {
  justify-content: flex-end;
  text-align: right;
}
</style>
